import React from "react"
import { twMerge } from "tailwind-merge"
import Card from "../shared/components/Card/Card"

/**
 * Properties for the FormattedCard component.
 *
 * @typedef FormattedCardProps
 * @property {React.ReactNode} children - The content of the card. Can be a WizardPage component (with hasNav=true), or any single page component (with nasNav=false).
 * @property {boolean} [hasNav=false] - If true, the card will have the needed styles to consume a WizardPage component as a child. If not, accepts any single page component as a child.
 * @property {string} [styleOverrides] - Override styles for the card.
 */
export interface FormattedCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  hasNav?: boolean
  styleOverrides?: string
}

/**
 * `FormattedCard` is a customizable card component that includes optional navigation.
 *
 * @component
 * @example
 * // Example usage of FormattedCard
 * <FormattedCard hasNav={false}>
 *   <p>Card content</p>
 * </FormattedCard>
 *
 * @param {FormattedCardProps} props - The properties that define the FormattedCard component.
 * @returns {React.Component} The FormattedCard component.
 */
export const FormattedCard: React.FC<FormattedCardProps> = ({
  children,
  hasNav = false,
  styleOverrides,
  ...rest
}) => {
  const marginStyle = "m-4"
  const paddingStyle = "p-[26px] pb-[64px]"
  const paddingTopOverride = hasNav ? "pt-0 pr-0 pl-0" : "pt-[16px]"
  const className = twMerge(
    marginStyle,
    paddingStyle,
    paddingTopOverride,
    styleOverrides,
  )

  return (
    <Card rounded styleOverrides={className} {...rest}>
      {children}
    </Card>
  )
}
